import React, { useEffect, useState } from "react"
import { Loader2 } from "lucide-react"
import { useToast } from "src/components/ui/toast"
import { fetchOverview } from "src/features/analytics/api/analyticsApi"
import { useReport } from "src/features/analytics/hooks/useReport"
import { presetToRange, formatINR, formatNumber } from "src/features/analytics/constants"
import ReportFilters from "src/features/analytics/components/ReportFilters"
import KpiCard from "src/features/analytics/components/KpiCard"
import EmptyReportState from "src/features/analytics/components/EmptyReportState"
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts"
import { CHART_COLORS } from "src/features/analytics/constants"

export default function OverviewReportPage() {
  const { toast } = useToast()
  const [preset, setPreset] = useState("30d")
  const [filters, setFilters] = useState(presetToRange("30d"))
  const { data, loading, fetch } = useReport(fetchOverview)

  useEffect(() => {
    fetch(filters).catch((err) => {
      if (err?.response?.status === 403) {
        toast({ variant: "destructive", title: "Error", description: "You don't have permission to view reports." })
      }
    })
  }, [filters.from, filters.to, filters.project_id])

  const kpis = data?.kpis

  return (
    <div className="mx-auto max-w-[1120px]">
      <ReportFilters
        filters={filters}
        onFiltersChange={setFilters}
        preset={preset}
        onPresetChange={setPreset}
      />

      {loading ? (
        <div className="flex items-center gap-2 p-8 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : !kpis ? (
        <EmptyReportState message="No data available for this period." />
      ) : (
        <>
          <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-3">
            <KpiCard
              label="New Leads"
              value={formatNumber(kpis.new_leads?.current)}
              delta={kpis.new_leads?.delta}
              deltaLabel="vs prior period"
            />
            <KpiCard
              label="Deals Won"
              value={formatNumber(kpis.deals_won?.current)}
              delta={kpis.deals_won?.delta}
              deltaLabel="vs prior period"
            />
            <KpiCard
              label="Win Rate"
              value={kpis.win_rate?.current != null ? `${kpis.win_rate.current}%` : "—"}
              delta={kpis.win_rate?.delta}
              deltaLabel="vs prior period"
            />
            <KpiCard
              label="Avg Days to Win"
              value={kpis.avg_days_to_win?.current ?? "—"}
              suffix="days"
              delta={kpis.avg_days_to_win?.delta != null ? -kpis.avg_days_to_win.delta : null}
              deltaLabel="vs prior period"
            />
            <KpiCard
              label="Collected"
              value={formatINR(kpis.collected?.current)}
              sublabel={kpis.collected?.attainment_pct != null ? `${kpis.collected.attainment_pct}% of target` : undefined}
            />
            <KpiCard
              label="Inventory Absorption"
              value={`${kpis.absorption_pct?.current ?? 0}%`}
              sublabel={`${kpis.absorption_pct?.absorbed ?? 0} of ${kpis.absorption_pct?.total_units ?? 0} units`}
            />
          </div>

          {/* Funnel sparkline */}
          {data.funnel_sparkline?.length > 0 && (
            <div className="mb-5 rounded-lg border border-slate-200 bg-white p-4">
              <div className="mb-3 text-[13px] font-medium text-slate-700">Pipeline Funnel</div>
              <div className="flex items-end gap-3">
                {data.funnel_sparkline.map((s, i) => {
                  const max = Math.max(...data.funnel_sparkline.map((x) => x.count), 1)
                  const pct = (s.count / max) * 100
                  return (
                    <div key={i} className="flex flex-1 flex-col items-center gap-1">
                      <span className="text-[12px] font-semibold text-slate-700">{s.count}</span>
                      <div className="w-full rounded-t" style={{ height: `${Math.max(pct, 8)}px`, backgroundColor: CHART_COLORS.primary, opacity: 1 - i * 0.15 }} />
                      <span className="text-[11px] text-slate-500 text-center">{s.name}</span>
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Collection trend */}
          {data.collection_trend?.length > 0 && (
            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <div className="mb-3 text-[13px] font-medium text-slate-700">Collection Trend</div>
              <ResponsiveContainer width="100%" height={200}>
                <AreaChart data={data.collection_trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => formatINR(v)} />
                  <Tooltip formatter={(v) => formatINR(v)} />
                  <Area
                    type="monotone"
                    dataKey="collected"
                    stroke={CHART_COLORS.collected}
                    fill={CHART_COLORS.primaryLight}
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </>
      )}
    </div>
  )
}
